import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { supabase } from "../../lib/supabaseClient";

const typeStyles = {
    info: {
        icon: "🐾",
        background: "#f7efe6",
        color: "#5d4037",
        border: "#d7ccc8",
    },
    success: {
        icon: "✓",
        background: "#eef8f1",
        color: "#2e6b3f",
        border: "#cce8d4",
    },
    warning: {
        icon: "!",
        background: "#fff8e1",
        color: "#7a5a00",
        border: "#f0d98c",
    },
    urgent: {
        icon: "!",
        background: "#fff0f0",
        color: "#9f1d1d",
        border: "#f0b8b8",
    },
};

export default function SiteNoticeBanner({
                                             primaryColor = "#8D6E63",
                                         }) {
    const [notice, setNotice] = useState(null);
    const [isHidden, setIsHidden] = useState(false);

    useEffect(() => {
        async function loadNotice() {
            const now = new Date().toISOString();

            const { data, error } = await supabase
                .from("site_notices")
                .select("*")
                .eq("active", true)
                .or(`starts_at.is.null,starts_at.lte.${now}`)
                .or(`ends_at.is.null,ends_at.gte.${now}`)
                .order("created_at", { ascending: false })
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error("Fehler beim Laden des Website-Hinweises:", error);
                return;
            }

            setNotice(data);
        }

        loadNotice();
    }, []);

    if (!notice || isHidden) return null;

    const currentType = typeStyles[notice.type] || typeStyles.info;

    return (
        <AnimatePresence>
            <motion.div
                style={{
                    width: "100%",
                    backgroundColor: currentType.background,
                    borderBottom: `1px solid ${currentType.border}`,
                    color: currentType.color,
                    position: "relative",
                    zIndex: 50,
                }}
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
            >
                <div
                    style={{
                        maxWidth: "1200px",
                        margin: "0 auto",
                        padding: "0.85rem clamp(1rem, 4vw, 2rem)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "1rem",
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "0.75rem",
                            minWidth: 0,
                        }}
                    >
                        <span
                            aria-hidden="true"
                            style={{
                                flex: "0 0 auto",
                                fontWeight: 800,
                            }}
                        >
                            {currentType.icon}
                        </span>

                        <div style={{ minWidth: 0 }}>
                            {notice.title && (
                                <strong
                                    style={{
                                        display: "block",
                                        marginBottom: "0.15rem",
                                        lineHeight: 1.35,
                                    }}
                                >
                                    {notice.title}
                                </strong>
                            )}

                            <span
                                style={{
                                    display: "block",
                                    lineHeight: 1.55,
                                    fontSize: "clamp(0.92rem, 2.4vw, 1rem)",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {notice.message}
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        aria-label="Hinweis schließen"
                        onClick={() => setIsHidden(true)}
                        style={{
                            flex: "0 0 auto",
                            width: "34px",
                            height: "34px",
                            borderRadius: "50%",
                            border: "none",
                            backgroundColor: `${primaryColor}18`,
                            color: currentType.color,
                            cursor: "pointer",
                            fontSize: "1.25rem",
                            lineHeight: 1,
                        }}
                    >
                        ×
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}